import { Link, NavLink, useMatch, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { signInWithGoogle, signOutUser } from "../services/auth";
import { useTheme } from "../hooks/useTheme";

import duliaLogoGold from "./Dulia2Gold.png";
import duliaLogoNavy from "./Dulia2Navy.png";

import { db } from "../firebase";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { fetchAllCalendarDays } from "../services/liturgical";
import { fetchAllSaints } from "../services/saints";

import {
  FaSearch,
  FaCalendarAlt,
  FaPlus,
  FaSignInAlt,
  FaSignOutAlt,
  FaDiceThree,
  FaBars,
  FaTimes,
  FaMoon,
  FaSun,
  FaInbox,
} from "react-icons/fa";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function dateIdFromParts(year, month0, day) {
  return `${pad2(day)}-${pad2(month0 + 1)}-${year}`;
}

function dateIdToSaintFeastKey(id) {
  const [dd = "", mm = ""] = String(id || "").split("-");
  if (!dd || !mm) return "";
  return `${mm}-${dd}`;
}

function parseSaintFeastKey(value) {
  const match = String(value || "").trim().match(/^(\d{1,2})-(\d{1,2})$/);
  if (!match) return null;

  const month = Number(match[1]);
  const day = Number(match[2]);

  if (!month || !day || month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  return {
    month,
    day,
    key: `${pad2(month)}-${pad2(day)}`,
  };
}

function getSaintFeastPairs(saint) {
  const keys = Array.isArray(saint?.feastKeys) ? saint.feastKeys : null;
  if (keys && keys.length) {
    return keys.map(parseSaintFeastKey).filter(Boolean);
  }

  if (saint?.feastKey) {
    const parsed = parseSaintFeastKey(saint.feastKey);
    return parsed ? [parsed] : [];
  }

  if (saint?.feastMonth && saint?.feastDay) {
    const month = Number(saint.feastMonth);
    const day = Number(saint.feastDay);

    if (!month || !day) return [];

    return [
      {
        month,
        day,
        key: `${pad2(month)}-${pad2(day)}`,
      },
    ];
  }

  return [];
}

function getColorMeta(color) {
  const value = String(color || "").toLowerCase();

  switch (value) {
    case "white":
      return { dot: "bg-white border border-slate-300", text: "text-slate-900" };
    case "red":
      return { dot: "bg-red-700", text: "text-white" };
    case "green":
      return { dot: "bg-green-700", text: "text-white" };
    case "violet":
      return { dot: "bg-purple-800", text: "text-white" };
    case "rose":
      return { dot: "bg-pink-400", text: "text-white" };
    case "black":
      return { dot: "bg-black", text: "text-white" };
    default:
      return { dot: "bg-dulia-navy", text: "text-white" };
  }
}

function celebrationColorClasses(color) {
  const meta = getColorMeta(color);
  return `${meta.dot} ${meta.text}`;
}

function TodayStrip({ loading, todayLabel, dayObj, saintFeasts }) {
  const celebrations = Array.isArray(dayObj?.celebrations) ? dayObj.celebrations : [];
  const saints = Array.isArray(saintFeasts) ? saintFeasts : [];
  const hasAnything =
    !!dayObj?.solemnity || !!dayObj?.feast || celebrations.length > 0 || saints.length > 0;

  const primaryColor = String(celebrations[0]?.color || "").toLowerCase();
  const isWhiteDay = primaryColor === "white";
  const isColoredDay = ["white", "red", "green", "violet", "rose", "black"].includes(primaryColor);

  const stripToneClasses = loading
    ? "border-dulia-line bg-white/70 text-dulia-nightSoft dark:border-dulia-lineStrong/20 dark:bg-dulia-nightSoft/95 dark:text-dulia-parchment"
    : primaryColor === "white"
    ? "border-slate-300 bg-white text-slate-900"
    : primaryColor === "red"
    ? "border-red-800 bg-red-700 text-white"
    : primaryColor === "green"
    ? "border-green-800 bg-green-700 text-white"
    : primaryColor === "violet"
    ? "border-purple-900 bg-purple-800 text-white"
    : primaryColor === "rose"
    ? "border-pink-500 bg-pink-400 text-white"
    : primaryColor === "black"
    ? "border-black bg-black text-white"
    : "border-dulia-line bg-white/70 text-dulia-nightSoft dark:border-dulia-lineStrong/20 dark:bg-dulia-nightSoft/95 dark:text-dulia-parchment";

  const calendarLinkClass = isColoredDay
    ? isWhiteDay
      ? "inline-flex items-center rounded-md px-1 py-0.5 text-xs font-medium text-slate-900 underline decoration-slate-500/60 underline-offset-4 transition hover:decoration-slate-900"
      : "inline-flex items-center rounded-md px-1 py-0.5 text-xs font-medium text-white underline decoration-white/70 underline-offset-4 transition hover:decoration-white"
    : "inline-flex items-center rounded-md px-1 py-0.5 text-xs font-medium text-dulia-nightSoft underline decoration-dulia-gold/60 underline-offset-4 transition hover:decoration-dulia-gold dark:text-dulia-goldSoft dark:hover:text-dulia-parchment";

  const rankPillClass = isColoredDay
    ? "rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-900 ring-1 ring-black/10"
    : "rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-900 ring-1 ring-black/10 dark:bg-dulia-panel dark:text-dulia-ink";

  const celebrationLinkClass = isColoredDay
    ? isWhiteDay
      ? "inline-flex rounded-md bg-black/5 px-2 py-1 text-xs font-semibold leading-tight whitespace-normal break-words text-slate-900 ring-1 ring-black/10"
      : "inline-flex rounded-md bg-white/15 px-2 py-1 text-xs font-semibold leading-tight whitespace-normal break-words text-white ring-1 ring-white/20"
    : "";

  const saintLinkClass = isColoredDay
    ? "inline-flex items-center rounded-md border border-black/10 bg-white/95 px-2 py-1 text-xs font-medium text-slate-900 underline decoration-slate-500/60 underline-offset-4 transition hover:decoration-slate-900"
    : "inline-flex items-center rounded-md border border-dulia-line bg-white/80 px-2 py-1 text-xs font-medium text-dulia-navy underline decoration-dulia-gold/60 underline-offset-4 transition hover:decoration-dulia-gold dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-goldSoft dark:hover:text-dulia-parchment";

  return (
    <div className={`mt-3 rounded-xl border px-3 py-2 shadow-soft ${stripToneClasses}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/calendar" className={calendarLinkClass}>
          Today · {todayLabel}
        </Link>

        {loading ? (
          <span className="text-xs opacity-80">Loading…</span>
        ) : hasAnything ? (
          <>
            {dayObj?.solemnity ? (
              <Link to="/calendar" className="inline-flex">
                <span className={rankPillClass}>Solemnity</span>
              </Link>
            ) : null}

            {dayObj?.feast ? (
              <Link to="/calendar" className="inline-flex">
                <span className={rankPillClass}>Feast</span>
              </Link>
            ) : null}

            {celebrations.map((item, index) => (
              <Link
                key={`${item?.name || "celebration"}-${item?.color || ""}-${index}`}
                to="/calendar"
                className={
                  isColoredDay
                    ? celebrationLinkClass
                    : `inline-flex rounded-md px-2 py-1 text-xs font-semibold leading-tight whitespace-normal break-words ${celebrationColorClasses(
                        item?.color
                      )}`
                }
                title={item?.name || "Untitled"}
              >
                {item?.name || "Untitled"}
              </Link>
            ))}

            {saints.map((saint, index) => {
              const name = String(saint?.name || "").trim();
              const id = String(saint?.id || "").trim();

              if (!id || !name) return null;

              return (
                <Link
                  key={`${id}-${index}`}
                  to={`/profile/${id}`}
                  className={saintLinkClass}
                  title={name}
                >
                  {name}
                </Link>
              );
            })}
          </>
        ) : (
          <Link to="/calendar" className={calendarLinkClass}>
            No celebrations listed today
          </Link>
        )}
      </div>
    </div>
  );
}

export default function Navbar() {
  const { user, loading, admin, adminLoading } = useAuth();
  const [open, setOpen] = useState(false);
  const [rolling, setRolling] = useState(false);
  const [diceRotation, setDiceRotation] = useState(0);

  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const [pendingCount, setPendingCount] = useState(0);

  const [calendarDays, setCalendarDays] = useState([]);
  const [saints, setSaints] = useState([]);
  const [todayLoading, setTodayLoading] = useState(true);

  const showSignInNote = !loading && !user;

  const profileMatch = useMatch("/profile/:id");
  const currentProfileId = profileMatch?.params?.id;

  const randomTo = currentProfileId
    ? `/random?exclude=${encodeURIComponent(currentProfileId)}`
    : "/random";

  const base =
    "inline-flex items-center gap-2 rounded-xl border border-transparent px-3 py-2 text-sm font-medium transition";
  const navBase =
    "text-dulia-navy hover:border-dulia-line hover:bg-dulia-panel dark:text-dulia-parchment dark:hover:border-dulia-lineStrong/30 dark:hover:bg-dulia-nightSoft";

  const linkClass = ({ isActive }) =>
    `${base} ${navBase} ${
      isActive
        ? "border-dulia-line bg-dulia-panel shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft"
        : ""
    }`;

  const utilityButton =
    `${base} border-dulia-line bg-dulia-panel text-dulia-navy shadow-soft ` +
    `hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft ` +
    `dark:text-dulia-parchment dark:hover:bg-dulia-night`;

  const today = useMemo(() => new Date(), []);
  const todayId = useMemo(
    () => dateIdFromParts(today.getFullYear(), today.getMonth(), today.getDate()),
    [today]
  );
  const todayLabel = useMemo(
    () =>
      today.toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    [today]
  );

  function closeMenu() {
    setOpen(false);
  }

  function handleRandomClick() {
    if (rolling) return;
    closeMenu();
    setRolling(true);
    setDiceRotation((prev) => prev + 360);

    window.setTimeout(() => {
      navigate(randomTo);
      setRolling(false);
    }, 450);
  }

  const canCreate = !!user && !!admin && !loading && !adminLoading;
  const canRequest = !!user && !admin && !loading && !adminLoading;
  const canViewRequests = !!user && !!admin && !loading && !adminLoading;

  useEffect(() => {
    if (!canViewRequests) {
      setPendingCount(0);
      return;
    }

    const q = query(collection(db, "saints"), where("approved", "==", false));
    const unsub = onSnapshot(
      q,
      (snap) => setPendingCount(snap.size || 0),
      () => setPendingCount(0)
    );

    return () => unsub();
  }, [canViewRequests]);

  useEffect(() => {
    let active = true;

    (async () => {
      setTodayLoading(true);

      try {
        const [days, saintDocs] = await Promise.all([
          fetchAllCalendarDays(),
          fetchAllSaints(),
        ]);

        if (!active) return;

        setCalendarDays(Array.isArray(days) ? days : []);
        setSaints(Array.isArray(saintDocs) ? saintDocs : []);
      } catch {
        if (!active) return;
        setCalendarDays([]);
        setSaints([]);
      } finally {
        if (active) setTodayLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const pendingLabel = pendingCount > 99 ? "99+" : String(pendingCount);

  const todayDay = useMemo(() => {
    return calendarDays.find((day) => day?.id === todayId) || null;
  }, [calendarDays, todayId]);

  const saintFeastsByKey = useMemo(() => {
    const map = new Map();

    for (const saint of saints) {
      const id = String(saint?.id || "").trim();
      const name = String(saint?.name || "").trim();
      if (!id || !name) continue;

      for (const pair of getSaintFeastPairs(saint)) {
        const key = String(pair?.key || "").trim();
        if (!key) continue;

        if (!map.has(key)) {
          map.set(key, []);
        }

        const items = map.get(key);
        if (!items.some((item) => item?.id === id)) {
          items.push({ id, name });
        }
      }
    }

    for (const items of map.values()) {
      items.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
    }

    return map;
  }, [saints]);

  const todaySaintFeasts = useMemo(() => {
    return saintFeastsByKey.get(dateIdToSaintFeastKey(todayId)) || [];
  }, [saintFeastsByKey, todayId]);

  return (
    <header className="border-b border-dulia-line/80 bg-dulia-parchment/90 backdrop-blur dark:border-dulia-lineStrong/20 dark:bg-dulia-night/95">
      <div className="mx-auto max-w-6xl px-4 py-3 md:px-6">
        <div className="flex items-center justify-between gap-3">
          <NavLink
            to="/"
            onClick={closeMenu}
            className="flex items-center gap-3 rounded-xl px-1 py-1 text-dulia-navy dark:text-dulia-parchment"
            aria-label="Dulia home"
          >
            <img
              src={isDark ? duliaLogoGold : duliaLogoNavy}
              alt="Dulia"
              className="h-9 w-9 object-contain"
              loading="lazy"
            />
            <div className="leading-none">
              <div className="font-bold tracking-[0.02em] text-dulia-nightSoft dark:text-white">
                Dulia
              </div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-dulia-nightSoft dark:text-dulia-goldSoft font-bold">
                A LIVING INDEX OF THE SAINTS
              </div>
            </div>
          </NavLink>

          <div className="hidden items-center gap-3 sm:flex">
            <nav className="flex flex-wrap gap-2">
              <NavLink to="/search" className={linkClass}>
                <FaSearch className="text-[14px]" aria-hidden="true" />
                Search
              </NavLink>

              <button
                type="button"
                onClick={handleRandomClick}
                className={`${base} ${navBase}`}
              >
                <FaDiceThree
                  className="text-[14px] transition-transform duration-500"
                  style={{ transform: `rotate(${diceRotation}deg)` }}
                  aria-hidden="true"
                />
                Random
              </button>

              <NavLink to="/calendar" className={linkClass}>
                <FaCalendarAlt className="text-[14px]" aria-hidden="true" />
                Calendar
              </NavLink>

              {canCreate ? (
                <NavLink to="/admin/new" className={linkClass}>
                  <FaPlus className="text-[14px]" aria-hidden="true" />
                  Create Profile
                </NavLink>
              ) : null}

              {canViewRequests ? (
                <NavLink to="/admin/requests" className={linkClass}>
                  <FaInbox className="text-[14px]" aria-hidden="true" />
                  <span className="inline-flex items-center gap-2">
                    Requests
                    {pendingCount > 0 ? (
                      <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-dulia-gold px-1.5 py-0.5 text-[11px] font-semibold leading-none text-dulia-night">
                        {pendingLabel}
                      </span>
                    ) : null}
                  </span>
                </NavLink>
              ) : null}

              {canRequest ? (
                <NavLink to="/request/" className={linkClass}>
                  <FaPlus className="text-[14px]" aria-hidden="true" />
                  Request Profile
                </NavLink>
              ) : null}
            </nav>

            <button
              type="button"
              className={utilityButton}
              onClick={toggleTheme}
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <FaSun aria-hidden="true" /> : <FaMoon aria-hidden="true" />}
            </button>

            {loading ? null : user ? (
              <button
                type="button"
                className={utilityButton}
                onClick={signOutUser}
                title={user.email || "Signed in"}
              >
                <FaSignOutAlt className="text-[14px]" aria-hidden="true" />
                Sign Out
              </button>
            ) : (
              <button
                type="button"
                className={utilityButton}
                onClick={signInWithGoogle}
              >
                <FaSignInAlt className="text-[14px]" aria-hidden="true" />
                Sign In
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 sm:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl border border-dulia-line bg-dulia-panel px-3 py-2 text-sm text-dulia-navy shadow-soft transition hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft dark:text-dulia-parchment dark:hover:bg-dulia-night"
              onClick={toggleTheme}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              title={isDark ? "Light mode" : "Dark mode"}
            >
              {isDark ? <FaSun aria-hidden="true" /> : <FaMoon aria-hidden="true" />}
            </button>

            {loading ? null : user ? (
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-xl border border-dulia-line bg-dulia-panel px-3 py-2 text-sm text-dulia-navy shadow-soft transition hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft dark:text-dulia-parchment dark:hover:bg-dulia-night"
                onClick={signOutUser}
                title={user.email || "Sign out"}
                aria-label="Sign out"
              >
                <FaSignOutAlt aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-xl border border-dulia-line bg-dulia-panel px-3 py-2 text-sm text-dulia-navy shadow-soft transition hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft dark:text-dulia-parchment dark:hover:bg-dulia-night"
                onClick={signInWithGoogle}
                aria-label="Sign in"
              >
                <FaSignInAlt aria-hidden="true" />
              </button>
            )}

            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl border border-dulia-line bg-dulia-panel px-3 py-2 text-sm text-dulia-navy shadow-soft transition hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft dark:text-dulia-parchment dark:hover:bg-dulia-night"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              {open ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
            </button>
          </div>
        </div>

        {showSignInNote ? (
          <p className="mt-2 text-xs text-dulia-nightSoft dark:text-dulia-goldSoft">
            Want to help? Sign in with Google to request new profiles.
          </p>
        ) : null}

        {open ? (
          <div className="sm:hidden">
            <nav className="mt-3 grid gap-2 rounded-2xl border border-dulia-line bg-dulia-panel p-2 shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft">
              <NavLink to="/search" className={linkClass} onClick={closeMenu}>
                <FaSearch className="text-[14px]" aria-hidden="true" />
                Search
              </NavLink>

              <button
                type="button"
                onClick={handleRandomClick}
                className={`${base} ${navBase} justify-start`}
              >
                <FaDiceThree
                  className="text-[14px] transition-transform duration-500"
                  style={{ transform: `rotate(${diceRotation}deg)` }}
                  aria-hidden="true"
                />
                Random
              </button>

              <NavLink to="/calendar" className={linkClass} onClick={closeMenu}>
                <FaCalendarAlt className="text-[14px]" aria-hidden="true" />
                Calendar
              </NavLink>

              {canCreate ? (
                <NavLink to="/admin/new" className={linkClass} onClick={closeMenu}>
                  <FaPlus className="text-[14px]" aria-hidden="true" />
                  Create Profile
                </NavLink>
              ) : null}

              {canViewRequests ? (
                <NavLink
                  to="/admin/requests"
                  className={linkClass}
                  onClick={closeMenu}
                >
                  <FaInbox className="text-[14px]" aria-hidden="true" />
                  <span className="inline-flex items-center gap-2">
                    Requests
                    {pendingCount > 0 ? (
                      <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-dulia-gold px-1.5 py-0.5 text-[11px] font-semibold leading-none text-dulia-night">
                        {pendingLabel}
                      </span>
                    ) : null}
                  </span>
                </NavLink>
              ) : null}

              {canRequest ? (
                <NavLink to="/request/" className={linkClass} onClick={closeMenu}>
                  <FaPlus className="text-[14px]" aria-hidden="true" />
                  Request Profile
                </NavLink>
              ) : null}
            </nav>
          </div>
        ) : null}

        <TodayStrip
          loading={todayLoading}
          todayLabel={todayLabel}
          dayObj={todayDay}
          saintFeasts={todaySaintFeasts}
        />
      </div>
    </header>
  );
}